import type { Attendance, StaffProfile } from "../services/staffService";

export type AttendanceFilters = {
  search: string;
  status: string;
  department: string;
  from: string;
  to: string;
};

export const EMPTY_ATTENDANCE_FILTERS: AttendanceFilters = {
  search: "",
  status: "",
  department: "",
  from: "",
  to: "",
};

export const ATTENDANCE_STATUSES: [string, string][] = [
  ["present", "Present"],
  ["late", "Late"],
  ["half_day", "Half day"],
  ["absent", "Absent"],
  ["leave", "On leave"],
];

export const ATTENDANCE_STATUS_TABS: [string, string][] = [["", "All"], ...ATTENDANCE_STATUSES];

export const ATTENDANCE_STATUS_DOTS: Record<string, string> = {
  present: "bg-green-500",
  late: "bg-amber-500",
  half_day: "bg-blue-500",
  absent: "bg-red-500",
  leave: "bg-gray-400",
};

/** Hours between two HH:MM times on the same day, to the quarter hour. */
export function hoursBetween(checkIn: string, checkOut: string) {
  const minutes = (time: string) => {
    const [hours, mins] = time.split(":").map(Number);
    return hours * 60 + mins;
  };
  if (!checkIn || !checkOut) return null;
  const worked = minutes(checkOut) - minutes(checkIn);
  if (!Number.isFinite(worked) || worked <= 0) return null;
  return Math.round((worked / 60) * 4) / 4;
}

/** "07:45:00" as "07:45". */
export const shortTime = (time?: string | null) => (time ? time.slice(0, 5) : "");

export function activeAttendanceFilterCount(filters: AttendanceFilters) {
  return Object.values(filters).filter((value) => value.trim()).length;
}

export function attendanceStatusCounts(records: Attendance[]) {
  const counts: Record<string, number> = { "": records.length };
  for (const record of records) counts[record.status] = (counts[record.status] ?? 0) + 1;
  return counts;
}

/** Records matching the filters, newest day first and then by name. */
export function filterAttendance(
  records: Attendance[],
  filters: AttendanceFilters,
  profiles: StaffProfile[] = [],
  { ignoreStatus = false } = {},
) {
  const departmentOf = new Map(profiles.map((profile) => [profile.id, profile.department]));
  const term = filters.search.trim().toLowerCase();
  return records
    .filter((record) => {
      const department = departmentOf.get(record.staff);
      return (
        (ignoreStatus || !filters.status || record.status === filters.status) &&
        (!term || [record.staff_name, record.notes ?? ""].some((value) => value.toLowerCase().includes(term))) &&
        (!filters.department ||
          (filters.department === "none" ? department == null : String(department ?? "") === filters.department)) &&
        (!filters.from || record.date >= filters.from) &&
        (!filters.to || record.date <= filters.to)
      );
    })
    .sort((a, b) => b.date.localeCompare(a.date) || a.staff_name.localeCompare(b.staff_name));
}

/** Monday of the week containing the given day, as YYYY-MM-DD. */
export function weekStart(now = new Date()) {
  const monday = new Date(now.getFullYear(), now.getMonth(), now.getDate() - ((now.getDay() + 6) % 7));
  const pad = (value: number) => String(value).padStart(2, "0");
  return `${monday.getFullYear()}-${pad(monday.getMonth() + 1)}-${pad(monday.getDate())}`;
}
